/**
 * Camera adapter.
 *
 * The webcam is an external boundary (per AGENTS.md), so the WebcamPanel
 * component depends on this interface — never on `navigator.mediaDevices`
 * directly. Tests pass a stub adapter; production code uses
 * `browserCameraAdapter` which talks to real WebRTC.
 */

export type CameraErrorKind =
  | 'permission-denied'
  | 'no-camera'
  | 'unsupported'
  | 'in-use'
  | 'unknown';

export interface CameraError {
  kind: CameraErrorKind;
  message: string;
}

export interface CameraAdapter {
  /** Opens a video stream. Throws a CameraError on failure. */
  open(): Promise<MediaStream>;
  /** Stops all tracks on the given stream. */
  close(stream: MediaStream): void;
  /**
   * Captures a single frame from a playing <video> element bound to a stream
   * and returns it as a Blob. The element must have non-zero dimensions
   * (i.e. metadata loaded).
   */
  capture(video: HTMLVideoElement): Promise<Blob>;
}

const CAPTURE_FALLBACK_DIMS = { width: 320, height: 240 };

export const browserCameraAdapter: CameraAdapter = {
  async open() {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      throw cameraError('unsupported', 'This browser does not support camera access.');
    }
    try {
      return await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: false,
      });
    } catch (err) {
      throw fromGetUserMediaError(err);
    }
  },

  close(stream) {
    for (const track of stream.getTracks()) {
      track.stop();
    }
  },

  async capture(video) {
    const width = video.videoWidth || CAPTURE_FALLBACK_DIMS.width;
    const height = video.videoHeight || CAPTURE_FALLBACK_DIMS.height;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw cameraError('unknown', 'Could not prepare a drawing surface.');
    }
    ctx.drawImage(video, 0, 0, width, height);
    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(cameraError('unknown', 'Could not save the picture.'));
      }, 'image/png');
    });
  },
};

export function isCameraError(err: unknown): err is CameraError {
  return (
    typeof err === 'object' &&
    err !== null &&
    'kind' in err &&
    'message' in err &&
    typeof (err as { kind: unknown }).kind === 'string'
  );
}

function cameraError(kind: CameraErrorKind, message: string): CameraError {
  return { kind, message };
}

function fromGetUserMediaError(err: unknown): CameraError {
  if (err && typeof err === 'object' && 'name' in err) {
    const name = (err as { name: string }).name;
    switch (name) {
      case 'NotAllowedError':
      case 'SecurityError':
        return cameraError(
          'permission-denied',
          'The camera is blocked. Allow camera access in your browser to use this.',
        );
      case 'NotFoundError':
      case 'OverconstrainedError':
        return cameraError('no-camera', 'No camera was found on this device.');
      case 'NotReadableError':
      case 'AbortError':
        return cameraError('in-use', 'The camera is busy. Close other apps using it and retry.');
      default:
        return cameraError('unknown', 'Could not turn on the camera.');
    }
  }
  return cameraError('unknown', 'Could not turn on the camera.');
}
